import { Lightning, Router } from '@lightningjs/sdk'

import PointerBackButton from '../../../components/NavigationPointers/PointerBackButton'
import { getColor, grey300, grey50, grey920, transparent } from '../../../constants/colors'
import { UniNeueBold, UniNeueHeavy, UniNeueRegular } from '../../../constants/fonts'
import { LEFT_SAFE_ZONE_MARGIN } from '../../../constants/general'
import { Images } from '../../../constants/paths'
import { AppConfig } from '../../../services/appConfig'
import { getText } from '../../../utils/dictionary'
import { getAsset } from '../../../utils/image'
import { getAppVersion } from '../../../utils/info'
import { generateQR } from '../../../utils/misc'
import { captureScreenData, NON_BFF_SEGMENT_SCREENS } from '../../../utils/segmentAnalytics'
import PageHandler from '../../handlers/pageHandler'

export default class CustomerSupport extends PageHandler {
  static _template() {
    return {
      w: 1920,
      h: 1080,
      rect: true,
      color: transparent,
      flex: {
        direction: 'row',
        paddingLeft: LEFT_SAFE_ZONE_MARGIN,
      },
      LeftContainer: {
        w: 872,
        h: 1080,
        rect: true,
        color: transparent,
        Title: {
          y: 48,
          text: {
            text: getText('customerSupport'),
            fontSize: 64,
            fontFace: UniNeueHeavy,
            textColor: getColor(grey50),
            lineHeight: 72,
          },
        },
        Icon: {
          x: 75,
          y: 328,
          texture: Lightning.Tools.getSvgTexture(
            getAsset(Images.SETTINGS_CUSTOMER_SUPPORT),
            472,
            436,
          ),
        },
        PointerBackButton: {
          visible: true,
          type: PointerBackButton,
          x: 1648,
          y: 48,
        },
      },
      RightContainer: {
        h: 1080,
        rect: true,
        color: transparent,
        InfoCard: {
          y: 200,
          w: 808,
          h: 800,
          rect: true,
          color: getColor(grey920),
          shader: {
            type: Lightning.shaders.RoundedRectangle,
            radius: 40,
          },
          flex: { direction: 'column', paddingTop: 40 },
          Header: {
            x: 40,
            text: {
              text: getText('customerSupportHeader'),
              fontSize: 48,
              fontFace: UniNeueBold,
              textColor: getColor(grey50),
              lineHeight: 56,
              wordWrapWidth: 420,
            },
          },
          QrCodeContainer: {
            x: 40,
            flex: { direction: 'row', alignItems: 'center' },
            flexItem: { marginTop: 40 },
            QRCode: {
              w: 244,
              h: 244,
              rect: true,
              color: getColor(grey50),
              shader: null,
              src: '',
              flexItem: { marginRight: 32 },
            },
            Content: {
              text: {
                text: getText('customerSupportDesc'),
                fontSize: 32,
                fontFace: UniNeueRegular,
                textColor: getColor(grey300),
                lineHeight: 36,
                wordWrapWidth: 360,
                maxLines: 4,
                maxLinesSuffix: '...',
              },
            },
          },
          Version: {
            x: 40,
            flexItem: { marginTop: 276 },
            text: {
              text: `${getText('versionTag')}${getAppVersion()}`,
              fontSize: 32,
              fontFace: UniNeueRegular,
              textColor: getColor(grey300),
              lineHeight: 36,
              advancedRenderer: true,
            },
          },
        },
      },
    }
  }

  get qrCode() {
    return this.tag('QRCode')
  }

  get pageText(): string {
    return getText('customerSupport')
  }

  get sectionText(): string {
    return (
      getText('customerSupportHeader') +
      ' . . ' +
      getText('customerSupportDesc') +
      ' . . ' +
      getText('versionTag') +
      getAppVersion()
    )
  }

  get pointerBackButton() {
    return this.tag('PointerBackButton') as PointerBackButton
  }

  async _init() {
    this.renderQrCode()
    void captureScreenData(NON_BFF_SEGMENT_SCREENS.Settings, NON_BFF_SEGMENT_SCREENS.Subscription)

    const { isCursorVisible } = AppConfig.shared.appSettings
    this.pointerBackButton.visible = isCursorVisible
    this.speak()
  }

  _handleLeft() {
    Router.focusWidget('Menu')
  }

  async renderQrCode() {
    await this.setQR(getText('customerSupportUrl'))
  }

  async setQR(textToQr: string) {
    this.qrCode.patch({ src: await generateQR(textToQr) })
  }

  _onCursorEnter() {
    const { isCursorVisible } = AppConfig.shared.appSettings
    this.pointerBackButton.visible = isCursorVisible
  }

  _onCursorLeave() {
    this.pointerBackButton.visible = false
  }
}
